import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { devLog, devError } from '../../../utils/devLogger';
import { toast } from 'react-toastify';
import CmsPopup from '../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../components/CmsPopupBtn';
import CmsPopupBtn from '../../../components/CmsPopupBtn';
import Modal from '../../../components/Modal';
import BidderFormPopup, { BidderFormData } from './BidderFormPopup';
import AuctionLotsList from './AuctionLotsList';
import CurrentLotInfo from './CurrentLotInfo';
import BidHistoryTable from './BidHistoryTable';
import { PopupContainer, RightSection, ModalButtonContainer } from './AuctionDetailStyles';
import { useAuctionSocket } from '../../../hooks/useAuctionSocket';
import { BanUserListResponse, ResponseModel } from '../../../types/socket.types';
import * as XLSX from 'xlsx';

// 경매 상세 데이터 타입
export interface AuctionDetailData {
  no: number;
  registrationDate: string;
  auctionHouse: string;
  auctionName: string;
  lotId: string;
  lotName: string;
  startPriceKrw: number;
  startPriceUsd: number;
  currentPriceKrw: number;
  currentPriceUsd: number;
  status: 'WAITING' | 'ONGOING' | 'COMPLETED' | 'CANCELLED';
}

// LOT 데이터 타입
export interface LotData {
  lotId: string;
  lotName: string;
  thumbnail: string;
  images?: string[]; // 소켓 데이터에서 받아온 이미지 배열
  status: 'WAITING' | 'ONGOING' | 'COMPLETED';
  startPriceKrw: number;
  startPriceUsd: number;
  currentPriceKrw: number;
  currentPriceUsd: number;
  bidHistory: BidHistoryItem[];
}

// 입찰 내역 아이템 타입
export interface BidHistoryItem {
  key: string;
  no: number;
  bidTime: string;
  bidder: string;
  siteNumber: string;
  country: string;
  bidAmountKrw: string;
  bidAmountUsd: string;
  contact: string;
  email: string;
  bidRestricted: string;
  isPreBidder: boolean; // 사전입찰자 여부
  isWon?: boolean; // 낙찰 여부
  user?: { // 원본 소켓 데이터의 user 정보 보존
    index: number;
    name: string | null;
    cellphone: string | null;
    email: string | null;
    code?: string;
    locationType: 'DOMESTIC' | 'OVERSEAS';
  } | null;
}

interface AuctionDetailPopupProps {
  isOpen: boolean;
  auctionData: AuctionDetailData | null;
  auctionIndex?: number; // 소켓 연결을 위한 auctionIndex 추가
  selectedLotIndex?: number; // 테이블에서 선택된 lot index 추가
  onClose: () => void;
}

const AuctionDetailPopup: React.FC<AuctionDetailPopupProps> = ({
  isOpen,
  auctionData,
  auctionIndex,
  selectedLotIndex: initialSelectedLotIndex,
  onClose,
}) => {
  // 디버그 모드 감지 및 로깅 함수
  const isDebugMode = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

  // 디버그 로깅 함수 - LiveBid와 동일한 패턴
  const debugLog = useCallback((message: string, data?: any) => {
    if (isDebugMode) {
      devLog(message, data);
    }
  }, [isDebugMode]);

  // React Strict Mode 중복 호출 감지 (개발 모드에서만)
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  if (isDebugMode && renderCountRef.current > 2) {
    debugLog('🔄 [AuctionDetailPopup] 과도한 리렌더링 감지:', {
      renderCount: renderCountRef.current,
      reason: 'React Strict Mode나 의존성 배열 문제일 수 있음'
    });
  }

  debugLog('🎯 [AuctionDetailPopup] 컴포넌트 렌더링:', {
    isOpen,
    auctionIndex,
    auctionDataExists: !!auctionData
  });

  // 소켓 연결 및 데이터 관리 - memoized auctionIndex
  const memoizedAuctionIndex = useMemo(() => auctionIndex, [auctionIndex]);
  
  const {
    isConnected,
    auctionData: socketAuctionData,
    bidHistory,
    // countdownStatus, // 카운트다운 상태 추가 - 현재 사용하지 않음
    currentLot: socketCurrentLot, // 소켓 이벤트로 관리되는 현재 진행 중인 lot
    isAuctionEnded, // 소켓 이벤트로 관리되는 경매 종료 상태
    getAuctionInfo,
    getBidHistory,
    startBid,
    cmsPlaceBid,
    banUser,
    unbanUser,
    getBanUserList,
    // nextLot, // 소켓 카운트다운으로 자동 처리되므로 사용하지 않음
    timerControl,
    getTimerStatus,
    auctionCountdown,
    connectSocket,
  } = useAuctionSocket({
    auctionIndex: memoizedAuctionIndex,
    autoConnect: true,
    autoRequestData: false, // 초기 자동 요청은 막되, 실시간 이벤트는 허용
    useAdminToken: true // CMS 페이지에서는 관리자 토큰 사용
  });

  debugLog('🔌 [AuctionDetailPopup] 소켓 상태:', {
    isConnected,
    hasSocketAuctionData: !!socketAuctionData,
    bidHistoryLength: bidHistory.length
  });

  // const [showEndAuctionModal, setShowEndAuctionModal] = useState(false);
  const [showCountModal, setShowCountModal] = useState(false);
  const [showBidderForm, setShowBidderForm] = useState(false);
  const [bidderFormData, setBidderFormData] = useState<BidderFormData>({
    bidAmount: '',
    bidNumber: '',
    name: '',
    phone: '',
    email: '',
    memo: ''
  });
  const [bidderFormErrors, setBidderFormErrors] = useState<Partial<BidderFormData>>({});
  const [isBidderEditMode, setIsBidderEditMode] = useState(false);
  const [bidRestrictions, setBidRestrictions] = useState<{[key: string]: string}>({});
  const [bannedUsers, setBannedUsers] = useState<number[]>([]); // 차단된 사용자 목록 상태 추가
  const [pendingBanActions, setPendingBanActions] = useState<{[userIndex: number]: 'ban' | 'unban'}>({});
  const [showBidRestrictionModal, setShowBidRestrictionModal] = useState(false);
  
  // bannedUsers 상태 변경 추적 (디버깅용)
  useEffect(() => {
    debugLog('🚫 [AuctionDetailPopup] bannedUsers 상태 변경:', {
      bannedUsers,
      length: bannedUsers.length
    });
  }, [bannedUsers, debugLog]);
  const [pendingRestrictionChange, setPendingRestrictionChange] = useState<{
    key: string;
    name: string;
    currentStatus: string;
  } | null>(null);
  const [selectedLotId, setSelectedLotId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'live' | 'pre'>('live'); // 라이브입찰/사전입찰 탭 상태
  
  // 사용자가 직접 선택했는지 여부를 추적하는 ref 추가
  const hasUserSelectedLot = useRef(false);
  const isInitialSetup = useRef(true); // 초기 설정 상태 추적
  const hasInitialized = useRef(false); // 팝업이 한 번 초기화되었는지 추적

  // 수동 요청 중복 방지를 위한 ref 추가 (실시간 소켓 이벤트는 제외)
  const hasManuallyRequestedAuctionInfo = useRef(false);
  const lastManuallyRequestedAuctionIndex = useRef<number | null>(null);

  // getAuctionInfo 함수를 ref로 저장하여 최신 함수 참조 유지
  const getAuctionInfoRef = useRef(getAuctionInfo);
  getAuctionInfoRef.current = getAuctionInfo;

  // 경매 정보 요청 타임아웃 처리
  const requestTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 소켓 자동 재연결 관리
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 팝업이 열릴 때 소켓 데이터 요청 - 수동 요청만 중복 방지 (실시간 소켓 이벤트는 항상 허용)
  const requestAuctionData = useCallback(() => {
    if (isOpen && memoizedAuctionIndex && isConnected) {
      // 이미 같은 auctionIndex로 수동 요청했으면 스킵 (하지만 소켓 실시간 이벤트는 허용)
      if (hasManuallyRequestedAuctionInfo.current && lastManuallyRequestedAuctionIndex.current === memoizedAuctionIndex) {
        debugLog('🏛️ [AuctionDetailPopup] 수동 요청 스킵 (이미 요청함, 실시간 이벤트는 허용):', {
          auctionIndex: memoizedAuctionIndex,
          lastRequestedAuctionIndex: lastManuallyRequestedAuctionIndex.current
        });
        return;
      }

      debugLog('🏛️ [AuctionDetailPopup] ✅ 모든 조건 만족 - 경매 정보 요청 실행:', memoizedAuctionIndex);
      
      hasManuallyRequestedAuctionInfo.current = true;
      lastManuallyRequestedAuctionIndex.current = memoizedAuctionIndex;
      getAuctionInfoRef.current(memoizedAuctionIndex);

      // 10초 후에도 응답이 없으면 재시도
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }
      
      requestTimeoutRef.current = setTimeout(() => {
        if (!socketAuctionData) {
          debugLog('⏰ [AuctionDetailPopup] 경매 정보 요청 타임아웃 - 재시도:', memoizedAuctionIndex);
          hasManuallyRequestedAuctionInfo.current = false;
          lastManuallyRequestedAuctionIndex.current = null;
          getAuctionInfoRef.current(memoizedAuctionIndex);
        }
      }, 10000);
      
    } else {
      debugLog('🏛️ [AuctionDetailPopup] ❌ 경매 정보 요청 조건 불충족:', {
        isOpen: `${isOpen} (${typeof isOpen})`,
        auctionIndex: `${memoizedAuctionIndex} (${typeof memoizedAuctionIndex})`,
        isConnected: `${isConnected} (${typeof isConnected})`
      });
    }

    // 연결이 끊어지면 플래그 리셋
    if (!isConnected) {
      hasManuallyRequestedAuctionInfo.current = false;
      lastManuallyRequestedAuctionIndex.current = null;
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
        requestTimeoutRef.current = null;
      }
    }
  }, [isOpen, memoizedAuctionIndex, isConnected, debugLog, socketAuctionData]);

  // 경매 데이터가 로드되면 타임아웃 타이머 정리
  useEffect(() => {
    if (socketAuctionData && requestTimeoutRef.current) {
      debugLog('✅ [AuctionDetailPopup] 경매 데이터 로드 완료 - 타임아웃 타이머 정리');
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
  }, [socketAuctionData, debugLog]);

  // requestAuctionData 실행
  useEffect(() => {
    requestAuctionData();
  }, [requestAuctionData]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, []);

  // 소켓 연결 지연/실패 시 자동 재연결 시도
  useEffect(() => {
    // 팝업이 닫혀 있거나 이미 연결된 경우: 재연결 상태 초기화
    if (!isOpen || isConnected) {
      reconnectAttemptsRef.current = 0;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      return;
    }

    // 최대 재시도 횟수 초과 시 더 이상 자동 재연결하지 않음 (사용자가 수동으로 다시 시도 가능)
    const MAX_RECONNECT_ATTEMPTS = 5;
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      if (isDebugMode) {
        devLog('🔁 [AuctionDetailPopup] 최대 자동 재연결 시도 횟수 초과', {
          attempts: reconnectAttemptsRef.current,
        });
      }
      return;
    }

    // 이미 예약된 타이머가 있으면 그대로 두고, 없으면 새로 설정
    if (!reconnectTimerRef.current) {
      const delayMs = 5000; // 5초 간격으로 재연결 시도

      if (isDebugMode) {
        devLog('🔄 [AuctionDetailPopup] 소켓 자동 재연결 타이머 설정', {
          nextAttemptInMs: delayMs,
          currentAttempts: reconnectAttemptsRef.current,
        });
      }

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        reconnectAttemptsRef.current += 1;

        if (isDebugMode) {
          devLog('🔄 [AuctionDetailPopup] 소켓 자동 재연결 시도', {
            attempt: reconnectAttemptsRef.current,
          });
        }

        connectSocket();
      }, delayMs);
    }

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [isOpen, isConnected, connectSocket, isDebugMode]);

  // 소켓 연결 성공 시 즉시 경매 정보 요청 (백업) - 실시간 소켓 이벤트는 항상 허용
  useEffect(() => {
    if (isConnected && memoizedAuctionIndex && isOpen) {
      // 이미 수동 요청했으면 스킵 (하지만 실시간 소켓 이벤트는 허용)
      if (hasManuallyRequestedAuctionInfo.current && lastManuallyRequestedAuctionIndex.current === memoizedAuctionIndex) {
        return;
      }

      debugLog('🚀 [AuctionDetailPopup] 소켓 연결 완료 - 즉시 경매 정보 요청:', memoizedAuctionIndex);
      
      // 더 긴 지연 후 요청 (소켓 완전 초기화 대기)
      const timer = setTimeout(() => {
        if (!hasManuallyRequestedAuctionInfo.current || lastManuallyRequestedAuctionIndex.current !== memoizedAuctionIndex) {
          debugLog('🏛️ [AuctionDetailPopup] 타이머 후 경매 정보 요청 실행:', memoizedAuctionIndex);
          hasManuallyRequestedAuctionInfo.current = true;
          lastManuallyRequestedAuctionIndex.current = memoizedAuctionIndex;
          getAuctionInfoRef.current(memoizedAuctionIndex);
        }
      }, 500); // 100ms → 500ms로 증가
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, memoizedAuctionIndex, isOpen, debugLog]);

  // 소켓 데이터를 UI에 맞는 형태로 변환 - CMS용 (상태 표시만 하고 자동 선택 없음)
  const auctionLots: LotData[] = useMemo(() => {
    if (!socketAuctionData?.lots) return [];

    // 사전입찰(preBids)에서 lot별 최고가를 미리 계산
    const maxPreBidPriceByLotIndex = new Map<number, number>();
    (socketAuctionData?.preBids || []).forEach((preBid: any) => {
      // 서버/소켓 payload에서 lotIndex 타입이 number|string로 올 수 있어 방어적으로 파싱
      const rawLotIndex = preBid?.lotIndex ?? preBid?.lot ?? preBid?.lotNo;
      const rawPrice = preBid?.price;

      const lotIndex = typeof rawLotIndex === 'number' ? rawLotIndex : Number(rawLotIndex);
      const price = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
      if (!Number.isFinite(lotIndex) || !Number.isFinite(price)) return;

      const prev = maxPreBidPriceByLotIndex.get(lotIndex) ?? 0;
      if (price > prev) maxPreBidPriceByLotIndex.set(lotIndex, price);
    });
    
    // disabled가 true인 로트들은 제외하고, 유효한 로트만 필터링
    const enabledLots = socketAuctionData.lots.filter(lot => 
      lot && typeof lot === 'object' && !lot.disabled && lot.lotNo !== undefined
    );
    
    // ended가 false인 로트들 중에서 가장 빠른 번호 찾기 (상태 표시용)
    const notEndedLots = enabledLots.filter(lot => !lot.ended);
    const ongoingLotNo = notEndedLots.length > 0 
      ? Math.min(...notEndedLots.map(lot => lot.lotNo))
      : null;

    // 실시간 업데이트 디버깅 로그
    if (isDebugMode) {
      devLog('🔄 [AuctionDetailPopup] auctionLots 실시간 업데이트:', {
        enabledLotsCount: enabledLots.length,
        notEndedLotsCount: notEndedLots.length,
        ongoingLotNo,
        socketCurrentLot: socketCurrentLot,
        currentSelectedLotId: selectedLotId,
        allLots: enabledLots.map(lot => ({
          lotNo: lot.lotNo,
          lotCode: lot.lotCode,
          ended: lot.ended,
          disabled: lot.disabled,
          status: lot.ended ? 'COMPLETED' : (lot.lotNo === ongoingLotNo ? 'ONGOING' : 'WAITING')
        })),
        endedLots: enabledLots.filter(lot => lot.ended).map(lot => ({
          lotNo: lot.lotNo,
          lotCode: lot.lotCode,
          ended: lot.ended
        }))
      });
    }
    
    return enabledLots.map(lot => {
      let status: 'WAITING' | 'ONGOING' | 'COMPLETED';
      
      // 소켓에서 받은 currentLot 정보를 기반으로 상태 결정 (우선순위 높음)
      if (socketCurrentLot && socketCurrentLot.lotNo) {
        if (lot.ended) {
          // ended가 true이면 무조건 완료
          status = 'COMPLETED';
        } else if (lot.lotNo === socketCurrentLot.lotNo) {
          // 소켓에서 받은 현재 진행 lot
          status = 'ONGOING';
        } else if (lot.lotNo < socketCurrentLot.lotNo) {
          // 현재 진행 lot보다 번호가 작으면 완료 (소켓 우선)
          status = 'COMPLETED';
        } else {
          // 현재 진행 lot보다 번호가 크면 대기
          status = 'WAITING';
        }
      } else {
        // 소켓 정보가 없는 경우 기존 로직 사용 (fallback)
        if (lot.ended) {
          status = 'COMPLETED';
        } else if (lot.lotNo === ongoingLotNo) {
          status = 'ONGOING';
        } else {
          status = 'WAITING';
        }
      }
      
      const startPriceKrw = lot.startPrice;
      const lotIndexKey = (lot as any).index ?? (lot as any).lotIndex;
      const normalizedLotIndexKey = typeof lotIndexKey === 'number' ? lotIndexKey : Number(lotIndexKey);
      const preBidMaxKrw = Number.isFinite(normalizedLotIndexKey)
        ? (maxPreBidPriceByLotIndex.get(normalizedLotIndexKey) ?? 0)
        : 0;
      // 사전입찰 최고가와 시작가 중 큰 값을 기준값으로 사용
      const preBidOrStartKrw = Math.max(startPriceKrw || 0, preBidMaxKrw);
      // live currentPrice가 있으면 그 값과 기준값 중 큰 값 사용
      const effectiveCurrentKrw = Math.max((lot.currentPrice || 0) as number, preBidOrStartKrw);

      return {
        lotId: lot.lotCode || `LOT${lot.lotNo.toString().padStart(3, '0')}`, // 서버 코드 우선, 없으면 기존 방식
        lotName: lot.lotName,
        thumbnail: lot.images?.[0] || 'thumbnail.jpg',
        images: lot.images || [], // 소켓에서 받은 이미지 배열 추가
        status,
        startPriceKrw,
        startPriceUsd: lot.startPriceUsd,
        // 완료된 lot의 경우 finalPrice가 있으면 사용, 없으면 currentPrice 사용
        currentPriceKrw: status === 'COMPLETED' 
          ? ((lot as any).finalPrice || lot.currentPrice || preBidOrStartKrw)
          : effectiveCurrentKrw,
        currentPriceUsd: status === 'COMPLETED'
          ? (((lot as any).finalPrice || lot.currentPrice || preBidOrStartKrw) / lot.exchangeRate)
          : (effectiveCurrentKrw / lot.exchangeRate),
        bidHistory: [] // 개별 lot의 입찰 내역은 선택 시 로드
      };
    });
  }, [socketAuctionData?.lots, socketAuctionData?.preBids, socketCurrentLot, selectedLotId, isDebugMode]);

  // 현재 선택된 LOT 데이터 가져오기 - CMS에서는 선택된 것만 표시 (최적화)
  const currentLot = useMemo(() => {
    // CMS에서는 사용자가 선택한 특정 lot만 표시 (자동으로 다른 lot으로 이동하지 않음)
    const lot = auctionLots.find(lot => lot.lotId === selectedLotId);
    if (!lot) return null;

    // 현재 선택된 lot의 index 찾기
    const selectedLotData = socketAuctionData?.lots?.find(socketLot => 
      socketLot && socketLot.lotNo !== undefined && 
      (socketLot.lotCode === selectedLotId || `LOT${socketLot.lotNo.toString().padStart(3, '0')}` === selectedLotId)
    );
    const currentLotIndex = selectedLotData?.index;

    // 현재 선택된 lot에 해당하는 입찰 내역만 필터링
    const filteredBidHistory = currentLotIndex !== undefined 
      ? bidHistory.filter(bid => bid.lot === currentLotIndex)
      : [];

    // 라이브 시작 전에도 사전입찰/입찰내역 최고가를 현재가에 반영 (현재 선택된 lot 기준)
    const maxPreBidPriceKrw = (() => {
      const allPreBids = socketAuctionData?.preBids || [];
      if (allPreBids.length === 0) return 0;

      return allPreBids.reduce((max: number, preBid: any) => {
        const rawLotIndex = preBid?.lotIndex ?? preBid?.lot ?? preBid?.lotNo;
        const lotIndex = typeof rawLotIndex === 'number' ? rawLotIndex : Number(rawLotIndex);
        if (!Number.isFinite(lotIndex) || lotIndex !== currentLotIndex) return max;

        const rawPrice = preBid?.price;
        const price = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
        if (!Number.isFinite(price)) return max;

        return Math.max(max, price);
      }, 0);
    })();

    const maxBidHistoryPriceKrw = (() => {
      if (!filteredBidHistory || filteredBidHistory.length === 0) return 0;
      return filteredBidHistory.reduce((max, bid: any) => {
        const price = typeof bid?.price === 'number' ? bid.price : Number(bid?.price);
        if (!Number.isFinite(price)) return max;
        return Math.max(max, price);
      }, 0);
    })();

    // debugLog 호출을 조건부로 실행 (useMemo 의존성에서 제외하기 위해)
    if (isDebugMode) {
      devLog('📊 [AuctionDetailPopup] 입찰 내역 필터링:', {
        selectedLotId,
        currentLotIndex,
        totalBidHistory: bidHistory.length,
        filteredBidHistory: filteredBidHistory.length
      });
    }

    // 입찰 내역을 UI 형태로 변환
    const transformedBidHistory: BidHistoryItem[] = filteredBidHistory.map((bid, index) => ({
      key: bid._id,
      no: index + 1,
      bidTime: new Date(bid.createAt).toLocaleString('ko-KR'),
      bidder: bid.user?.name || '사전입찰자', // 사전입찰자는 user 정보가 없음
      siteNumber: bid.code,
      country: bid.user?.locationType === 'DOMESTIC' ? '대한민국' : '해외',
      bidAmountKrw: `₩${bid.price.toLocaleString()}`,
      bidAmountUsd: `$${(bid.price / bid.exchangeRate).toLocaleString()}`,
      contact: bid.user?.cellphone || '',
      email: bid.user?.email || '',
      bidRestricted: bidRestrictions[bid._id] || 'N',
      isPreBidder: !bid.user, // user 정보가 없으면 사전입찰자
      isWon: bid.isWon, // 낙찰 여부 추가
      user: bid.user // 원본 소켓 데이터의 user 정보 보존
    }));

    const exchangeRate = selectedLotData?.exchangeRate || 1200;
    const derivedCurrentPriceKrw =
      lot.status === 'COMPLETED'
        ? lot.currentPriceKrw
        : Math.max(
            lot.startPriceKrw || 0,
            lot.currentPriceKrw || 0,
            maxPreBidPriceKrw || 0,
            maxBidHistoryPriceKrw || 0
          );

    if (isDebugMode) {
      devLog('💰 [AuctionDetailPopup] 현재가 계산 디버깅:', {
        selectedLotId,
        currentLotIndex,
        startPriceKrw: lot.startPriceKrw,
        mappedCurrentPriceKrw: lot.currentPriceKrw,
        maxPreBidPriceKrw,
        maxBidHistoryPriceKrw,
        derivedCurrentPriceKrw,
        exchangeRate,
        preBidsLength: socketAuctionData?.preBids?.length || 0,
        filteredBidHistoryLength: filteredBidHistory.length
      });
    }

    return {
      ...lot,
      ...(lot.status === 'COMPLETED'
        ? {}
        : {
            currentPriceKrw: derivedCurrentPriceKrw,
            currentPriceUsd: derivedCurrentPriceKrw / exchangeRate
          }),
      bidHistory: transformedBidHistory
    };
  }, [
    auctionLots, 
    selectedLotId, 
    bidHistory, 
    bidRestrictions, 
    socketAuctionData?.lots,
    socketAuctionData?.preBids,
    isDebugMode
    // debugLog 제거 - 함수는 의존성에서 제외 (매번 새로 생성되어 리렌더링 유발)
  ]);

  // 사전입찰 데이터를 BidHistoryItem 형태로 변환
  const preBidHistory = useMemo(() => {
    if (!socketAuctionData?.preBids || !selectedLotId) return [];

    // 현재 선택된 lot의 실제 데이터 찾기
    const selectedLotData = socketAuctionData?.lots?.find(socketLot => 
      socketLot && socketLot.lotNo !== undefined && 
      (socketLot.lotCode === selectedLotId || `LOT${socketLot.lotNo.toString().padStart(3, '0')}` === selectedLotId)
    );

    if (!selectedLotData) return [];

    // 현재 LOT에 해당하는 사전입찰만 필터링
    const lotPreBids = socketAuctionData.preBids.filter((preBid: any) => 
      preBid.lotIndex === selectedLotData.index
    );

    // BidHistoryItem 형태로 변환
    const transformedPreBids: BidHistoryItem[] = lotPreBids.map((preBid: any, index: number) => ({
      key: preBid.index.toString(),
      no: index + 1,
      bidTime: new Date(preBid.createAt).toLocaleString('ko-KR'),
      bidder: preBid.anonymousCode || preBid.user?.name || '사전입찰자',
      siteNumber: `F${preBid.index.toString().padStart(4, '0')}`, // 사전입찰자는 F로 시작
      country: '대한민국', // 기본값
      bidAmountKrw: `₩${preBid.price.toLocaleString()}`,
      bidAmountUsd: `$${(preBid.price / (selectedLotData.exchangeRate || 1200)).toFixed(2)}`,
      contact: '-', // 사전입찰에서는 연락처 정보 없음
      email: '-', // 사전입찰에서는 이메일 정보 없음
      bidRestricted: 'N', // 사전입찰은 제한 없음
      status: preBid.isWon ? '낙찰' : '진행중',
      isPreBidder: true,
      isWon: preBid.isWon,
      user: preBid.user
    }));

    if (isDebugMode) {
      devLog('📊 [AuctionDetailPopup] 사전입찰 데이터 변환:', {
        selectedLotId,
        selectedLotData,
        totalPreBids: socketAuctionData.preBids?.length || 0,
        filteredPreBids: lotPreBids.length,
        transformedPreBids: transformedPreBids.length
      });
    }

    return transformedPreBids;
  }, [socketAuctionData?.preBids, socketAuctionData?.lots, selectedLotId, isDebugMode]);
  
  // 팝업이 처음 열릴 때만 초기화 (리렌더링으로 인한 중복 초기화 방지)
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      // 팝업이 처음 열릴 때만 상태 초기화
      hasUserSelectedLot.current = false;
      isInitialSetup.current = true;
      hasInitialized.current = true;
      
      debugLog('🚀 [AuctionDetailPopup] 팝업 최초 열림 - 상태 초기화');
      
      // initialSelectedLotIndex가 있는 경우에만 초기 선택 설정
      if (initialSelectedLotIndex !== undefined && socketAuctionData?.lots) {
        const targetLot = socketAuctionData.lots.find(lot => 
          lot && lot.index === initialSelectedLotIndex
        );
        if (targetLot && targetLot.lotNo !== undefined) {
          const targetLotId = targetLot.lotCode || `LOT${targetLot.lotNo.toString().padStart(3, '0')}`;
          setSelectedLotId(targetLotId);
          debugLog('🎯 [AuctionDetailPopup] CMS 팝업 최초 열림 - 초기 LOT 설정:', {
            initialSelectedLotIndex,
            targetLotId
          });
        }
      }
    } else if (!isOpen) {
      // 팝업이 닫힐 때만 초기화 상태 리셋 (다음 열림을 위해)
      hasInitialized.current = false;
      debugLog('🔒 [AuctionDetailPopup] 팝업 닫힘 - 다음 열림을 위해 초기화 상태 리셋');
    }
  }, [isOpen, initialSelectedLotIndex, socketAuctionData?.lots, debugLog]);

  // CMS에서 소켓 이벤트에 따른 자동 lot 전환 구현 (소켓 lot 변경시에만)
  const prevSocketCurrentLot = useRef<{ lotIndex: number; lotNo?: number } | null>(null);
  
  useEffect(() => {
    // 소켓 이벤트로 받은 currentLot이 있고, 소켓 데이터가 있을 때 (경매 종료 상관없이)
    if (socketCurrentLot && socketCurrentLot.lotIndex && socketAuctionData?.lots) {
      const currentSocketLot = { lotIndex: socketCurrentLot.lotIndex, lotNo: socketCurrentLot.lotNo };
      
      // 소켓 lot가 실제로 변경되었는지 확인
      const isSocketLotChanged = !prevSocketCurrentLot.current || 
                                prevSocketCurrentLot.current.lotIndex !== currentSocketLot.lotIndex ||
                                prevSocketCurrentLot.current.lotNo !== currentSocketLot.lotNo;
      
      if (isSocketLotChanged) {
        // 이전 소켓 lot 정보 업데이트
        prevSocketCurrentLot.current = currentSocketLot;
        
        // 소켓에서 받은 lotIndex로 lot 찾기
        const targetLot = socketAuctionData.lots.find(lot => 
          lot && lot.index === socketCurrentLot.lotIndex
        );
        
        if (targetLot && targetLot.lotNo !== undefined) {
          const targetLotId = targetLot.lotCode || `LOT${targetLot.lotNo.toString().padStart(3, '0')}`;
          
          debugLog('🔄 [AuctionDetailPopup] 소켓 lot 변경으로 인한 자동 전환:', {
            from: selectedLotId,
            to: targetLotId,
            socketLotChange: {
              prev: prevSocketCurrentLot.current,
              current: currentSocketLot
            },
            reason: '소켓에서 새로운 lot 시작',
            isAuctionEnded,
            targetLotInfo: {
              index: targetLot.index,
              lotNo: targetLot.lotNo,
              lotCode: targetLot.lotCode,
              ended: targetLot.ended
            }
          });
          
          // 선택된 lot가 다를 때만 업데이트
          if (selectedLotId !== targetLotId) {
            setSelectedLotId(targetLotId);
            hasUserSelectedLot.current = false; // 소켓 이벤트로 인한 전환이므로 사용자 선택 상태 해제
            
            // 입찰 내역 요청 플래그 리셋 (새로운 lot의 입찰 내역을 로드하기 위해)
            hasRequestedBidHistory.current = false;
            lastRequestedLotIndex.current = null;
            
            debugLog('✅ [AuctionDetailPopup] lot 자동 전환 완료:', targetLotId);
          } else {
            debugLog('ℹ️ [AuctionDetailPopup] 이미 선택된 lot와 동일하여 업데이트 생략:', targetLotId);
          }
        } else {
          debugLog('⚠️ [AuctionDetailPopup] 소켓 lot에 해당하는 데이터를 찾을 수 없음:', {
            socketLotIndex: socketCurrentLot.lotIndex,
            availableLots: socketAuctionData.lots.map(lot => ({
              index: lot.index,
              lotNo: lot.lotNo,
              lotCode: lot.lotCode
            }))
          });
        }
      }
    }
  }, [socketCurrentLot, socketAuctionData?.lots, selectedLotId, isAuctionEnded, debugLog]);

  // CMS에서 초기 lot 설정 (팝업 열릴 때만)
  useEffect(() => {
    // 사용자가 직접 선택한 경우에는 initialSelectedLotIndex를 무시
    if (hasUserSelectedLot.current) {
      debugLog('👤 [AuctionDetailPopup] 사용자 선택 우선 - initialSelectedLotIndex 무시', initialSelectedLotIndex);
      return;
    }

    // 팝업이 열려있고, 아직 초기 설정 상태이며, 필요한 데이터가 모두 있는 경우에만 실행
    if (!isOpen || !isInitialSetup.current || !socketAuctionData?.lots || auctionLots.length === 0) {
      return;
    }

    // initialSelectedLotIndex가 있는 경우 해당 LOT 선택
    if (initialSelectedLotIndex !== undefined) {
      const targetLot = socketAuctionData.lots.find(lot => 
        lot && lot.index === initialSelectedLotIndex
      );
      if (targetLot && targetLot.lotNo !== undefined) {
        const targetLotId = targetLot.lotCode || `LOT${targetLot.lotNo.toString().padStart(3, '0')}`;
        if (auctionLots.some(lot => lot.lotId === targetLotId)) {
          // 현재 선택과 다른 경우에만 설정
          if (selectedLotId !== targetLotId) {
            setSelectedLotId(targetLotId);
            isInitialSetup.current = false; // 초기 설정 완료
            debugLog('🎯 [AuctionDetailPopup] CMS 초기 LOT 선택:', {
              initialSelectedLotIndex,
              targetLotId,
              previousLotId: selectedLotId
            });
          }
          return;
        }
      }
    }
    
    // initialSelectedLotIndex가 없거나 유효하지 않은 경우 기본값 설정
    if (!selectedLotId) {
      setSelectedLotId(auctionLots[0].lotId);
      isInitialSetup.current = false; // 초기 설정 완료
      debugLog('🎯 [AuctionDetailPopup] 기본 첫 번째 LOT 선택:', auctionLots[0].lotId);
    }
  }, [isOpen, initialSelectedLotIndex, socketAuctionData?.lots, auctionLots, selectedLotId, debugLog]);

  // useAuctionSocket에서 이미 next-lot과 bid-started 이벤트를 구독하고 있으므로
  // 별도로 주기적인 업데이트나 추가 이벤트 구독은 불필요함

  // 입찰 내역 요청 중복 방지를 위한 ref 추가
  const hasRequestedBidHistory = useRef(false);
  const lastRequestedLotIndex = useRef<number | null>(null);

  // getBidHistory 함수를 ref로 저장하여 최신 함수 참조 유지
  const getBidHistoryRef = useRef(getBidHistory);
  getBidHistoryRef.current = getBidHistory;

  // 경매 종료 시 입찰내역 업데이트 콜백
  const handleAuctionEnded = useCallback(() => {
    debugLog('🏁 [AuctionDetailPopup] 경매 종료 - 입찰내역 업데이트');
    
    // 현재 선택된 lot의 입찰내역을 다시 요청
    if (memoizedAuctionIndex && selectedLotId && socketAuctionData?.lots) {
      const selectedLotData = socketAuctionData.lots.find(socketLot => 
        socketLot && socketLot.lotNo !== undefined && 
        (socketLot.lotCode === selectedLotId || `LOT${socketLot.lotNo.toString().padStart(3, '0')}` === selectedLotId)
      );
      const currentLotIndex = selectedLotData?.index;
      
      if (currentLotIndex !== undefined) {
        debugLog('🔄 [AuctionDetailPopup] 경매 종료 후 입찰내역 재요청:', {
          auctionIndex: memoizedAuctionIndex,
          lotIndex: currentLotIndex,
          selectedLotId
        });
        
        setTimeout(() => {
          getBidHistoryRef.current(memoizedAuctionIndex, currentLotIndex);
        }, 1000); // 서버에서 최종 데이터 처리 완료 대기
      }
    }
  }, [memoizedAuctionIndex, selectedLotId, socketAuctionData?.lots, debugLog]);

  // LOT 선택 시 해당 LOT의 입찰 내역 조회 - 최적화된 버전 + 중복 방지
  const selectedLotData = useMemo(() => {
    if (!selectedLotId || !socketAuctionData?.lots) return null;
    const lot = socketAuctionData.lots.find(lot => 
      lot && lot.lotNo !== undefined && 
      (lot.lotCode === selectedLotId || `LOT${lot.lotNo.toString().padStart(3, '0')}` === selectedLotId)
    );
    return lot || null;
  }, [selectedLotId, socketAuctionData?.lots]);

  useEffect(() => {
    if (selectedLotData?.index !== undefined && memoizedAuctionIndex && isConnected) {
      // 이미 같은 lotIndex로 요청했으면 스킵 (단, 소켓 이벤트로 리셋된 경우는 제외)
      if (hasRequestedBidHistory.current && lastRequestedLotIndex.current === selectedLotData.index) {
        debugLog('📚 [AuctionDetailPopup] 입찰 내역 조회 스킵 (이미 요청함):', {
          auctionIndex: memoizedAuctionIndex,
          lotIndex: selectedLotData.index,
          lastRequestedLotIndex: lastRequestedLotIndex.current
        });
        return;
      }

      debugLog('📚 [AuctionDetailPopup] 입찰 내역 조회:', { 
        auctionIndex: memoizedAuctionIndex, 
        lotIndex: selectedLotData.index,
        selectedLotId: selectedLotId,
        reason: '새로운 lot 선택 또는 소켓 이벤트로 인한 리셋'
      });
      
      hasRequestedBidHistory.current = true;
      lastRequestedLotIndex.current = selectedLotData.index;
      getBidHistoryRef.current(memoizedAuctionIndex, selectedLotData.index);
    }

    // 선택된 LOT가 없어지면 플래그 리셋
    if (!selectedLotData) {
      hasRequestedBidHistory.current = false;
      lastRequestedLotIndex.current = null;
      debugLog('🧹 [AuctionDetailPopup] 선택된 lot 없음 - 입찰 내역 플래그 리셋');
    }
  }, [selectedLotData, memoizedAuctionIndex, isConnected, selectedLotId, debugLog]);

  // ban-user, unban-user, ban-user-list 이벤트 핸들러
  useEffect(() => {
    const handleBanUserEvent = (data: any) => {
      debugLog('🚫 [AuctionDetailPopup] ban-user 이벤트 수신:', data);
      
      // 사용자 차단 성공/실패에 따른 토스트 메시지
      if (data && data.statusCode === 200) {
        toast.success('사용자가 성공적으로 차단되었습니다.');
        debugLog('✅ [AuctionDetailPopup] 차단 성공 - 로컬 상태 유지');
        
        // 성공 시 대기 중인 액션에서 제거
        if (data.data?.userIndex) {
          setPendingBanActions(prev => {
            const { [data.data.userIndex]: removed, ...rest } = prev;
            return rest;
          });
        }
      } else {
        const errorMessage = data?.message || '사용자 차단에 실패했습니다.';
        toast.error(errorMessage);
        
        // 실패한 경우 롤백: 대기 중인 모든 'ban' 액션을 되돌림
        setPendingBanActions(prev => {
          const failedActions = Object.entries(prev).filter(([_, action]) => action === 'ban');
          failedActions.forEach(([userIndexStr, _]) => {
            const userIndex = parseInt(userIndexStr);
            setBannedUsers(prevBanned => {
              const rolledBack = prevBanned.filter(idx => idx !== userIndex);
              debugLog('🔄 [AuctionDetailPopup] 차단 실패 - 상태 롤백:', {
                userIndex,
                before: prevBanned,
                after: rolledBack
              });
              return rolledBack;
            });
          });
          
          // 실패한 액션들 제거
          const newPending = { ...prev };
          failedActions.forEach(([userIndexStr, _]) => {
            delete newPending[parseInt(userIndexStr)];
          });
          return newPending;
        });
      }
    };

    const handleUnbanUserEvent = (data: any) => {
      debugLog('✅ [AuctionDetailPopup] unban-user 이벤트 수신:', data);
      
      // 사용자 차단 해제 성공/실패에 따른 토스트 메시지
      if (data && data.statusCode === 200) {
        toast.success('사용자 차단이 성공적으로 해제되었습니다.');
        debugLog('✅ [AuctionDetailPopup] 차단 해제 성공 - 로컬 상태 유지');
        
        // 성공 시 대기 중인 액션에서 제거
        if (data.data?.userIndex) {
          setPendingBanActions(prev => {
            const { [data.data.userIndex]: removed, ...rest } = prev;
            return rest;
          });
        }
      } else {
        const errorMessage = data?.message || '사용자 차단 해제에 실패했습니다.';
        toast.error(errorMessage);
        
        // 실패한 경우 롤백: 대기 중인 모든 'unban' 액션을 되돌림
        setPendingBanActions(prev => {
          const failedActions = Object.entries(prev).filter(([_, action]) => action === 'unban');
          failedActions.forEach(([userIndexStr, _]) => {
            const userIndex = parseInt(userIndexStr);
            setBannedUsers(prevBanned => {
              if (!prevBanned.includes(userIndex)) {
                const rolledBack = [...prevBanned, userIndex];
                debugLog('🔄 [AuctionDetailPopup] 차단 해제 실패 - 상태 롤백:', {
                  userIndex,
                  before: prevBanned,
                  after: rolledBack
                });
                return rolledBack;
              }
              return prevBanned;
            });
          });
          
          // 실패한 액션들 제거
          const newPending = { ...prev };
          failedActions.forEach(([userIndexStr, _]) => {
            delete newPending[parseInt(userIndexStr)];
          });
          return newPending;
        });
      }
    };

    const handleBanUserListEvent = (response: ResponseModel<BanUserListResponse>) => {
      debugLog('📋 [AuctionDetailPopup] ban-user-list 이벤트 수신:', response);
      debugLog('📋 [AuctionDetailPopup] ban-user-list 핸들러 실행 시작!');
      
      if (response.statusCode === 200 && response.data) {
        const bannedUserIndexes = response.data.banUsers;
        
        debugLog('📋 [AuctionDetailPopup] 차단된 사용자 목록 처리:', {
          bannedUserIndexes,
          beforeUpdate: bannedUsers
        });
        
        // 차단된 사용자 목록 상태 업데이트
        setBannedUsers(bannedUserIndexes);
        
        debugLog('📋 [AuctionDetailPopup] setBannedUsers 호출 완료:', bannedUserIndexes);
        
        // 차단된 사용자들의 입찰을 모두 제한 상태로 설정 (기존 로직 유지)
        setBidRestrictions(prev => {
          const newRestrictions = { ...prev };
          
          // 기존 제한 상태를 초기화하고 차단된 사용자들만 설정
          bidHistory.forEach(bid => {
            if ('user' in bid && bid.user && bid.user.index !== undefined) {
              if (bannedUserIndexes.includes(bid.user.index)) {
                newRestrictions[bid._id] = 'Y';
              } else {
                delete newRestrictions[bid._id];
              }
            }
          });
          
          return newRestrictions;
        });
        
        debugLog('✅ [AuctionDetailPopup] 차단된 사용자 목록 기반으로 입찰제한 상태 업데이트:', {
          bannedUserIndexes,
          totalBidHistory: bidHistory.length,
          affectedBids: bidHistory.filter(bid => 
            'user' in bid && bid.user && bid.user.index !== undefined && 
            bannedUserIndexes.includes(bid.user.index)
          ).length
        });
      } else {
        debugLog('❌ [AuctionDetailPopup] ban-user-list 응답 오류:', response);
      }
    };

    // 소켓 서비스에 이벤트 리스너 등록
    if (typeof window !== 'undefined') {
      const auctionSocketService = require('../../../services/auctionSocketService').default;
      auctionSocketService.on('ban-user', handleBanUserEvent);
      auctionSocketService.on('unban-user', handleUnbanUserEvent);
      auctionSocketService.on('ban-user-list', handleBanUserListEvent);
      
      // 추가 디버깅: ban-user-list 이벤트 리스너 등록 확인
      debugLog('🔗 [AuctionDetailPopup] ban-user-list 이벤트 리스너 등록 완료');
      
      return () => {
        auctionSocketService.off('ban-user', handleBanUserEvent);
        auctionSocketService.off('unban-user', handleUnbanUserEvent);
        auctionSocketService.off('ban-user-list', handleBanUserListEvent);
        debugLog('🔗 [AuctionDetailPopup] 이벤트 리스너 정리 완료');
      };
    }
  }, [bidHistory, bannedUsers, pendingBanActions, debugLog]);

  // 경매에 연결되었고 입찰 내역이 있을 때 차단된 사용자 목록 조회
  useEffect(() => {
    if (isConnected && memoizedAuctionIndex && bidHistory.length > 0) {
      debugLog('📋 [AuctionDetailPopup] 차단된 사용자 목록 조회 요청:', {
        auctionIndex: memoizedAuctionIndex,
        bidHistoryLength: bidHistory.length
      });
      getBanUserList(memoizedAuctionIndex);
    }
  }, [isConnected, memoizedAuctionIndex, bidHistory.length, getBanUserList, debugLog]);
  
  // LOT 선택 핸들러 - 사용자가 직접 선택했음을 표시
  const handleLotSelect = useCallback((lotId: string) => {
    debugLog('👤 [AuctionDetailPopup] 사용자가 직접 LOT 선택:', lotId);
    hasUserSelectedLot.current = true; // 사용자가 직접 선택했음을 표시
    setSelectedLotId(lotId);
  }, [debugLog]);

  // 탭 전환 핸들러
  const handleTabChange = useCallback((tab: 'live' | 'pre') => {
    debugLog('📋 [AuctionDetailPopup] 탭 전환:', tab);
    setActiveTab(tab);
  }, [debugLog]);

  // 경매 종료 모달 열기
  // const handleEndAuctionClick = useCallback(() => {
  //   setShowEndAuctionModal(true);
  // }, []);

  // 경매 종료 확인 (다음 작품으로 이동) - 소켓 카운트다운으로 자동 처리되므로 주석처리
  // const handleEndAuctionConfirm = useCallback(() => {
  //   setShowEndAuctionModal(false);
  //   if (memoizedAuctionIndex) {
  //     debugLog('⏭️ [AuctionDetailPopup] 다음 작품으로 이동:', memoizedAuctionIndex);
  //     nextLot(memoizedAuctionIndex);
  //   }
  // }, [memoizedAuctionIndex, nextLot, debugLog]);

  // 경매 종료 취소
  // const handleEndAuctionCancel = useCallback(() => {
  //   setShowEndAuctionModal(false);
  // }, []);

  // 20초 카운트 모달 열기
  const handleCountClick = useCallback(() => {
    setShowCountModal(true);
  }, []);

  // 20초 카운트 시작 확인 - 최적화된 버전
  const handleCountConfirm = useCallback(() => {
    setShowCountModal(false);
    
    if (memoizedAuctionIndex && selectedLotData?.index !== undefined) {
      debugLog('⏰ [AuctionDetailPopup] 새로운 타이머 제어로 카운트다운 시작:', {
        auctionIndex: memoizedAuctionIndex,
        lotIndex: selectedLotData.index,
        duration: 20
      });
      
      // 새로운 타이머 제어 방식 사용
      timerControl(memoizedAuctionIndex, selectedLotData.index, 'start', 20);
      
      // 타이머 상태도 조회
      getTimerStatus(memoizedAuctionIndex, selectedLotData.index);
    } else {
      debugLog('❌ [AuctionDetailPopup] 필요한 데이터가 없어서 기존 방식 사용');
      debugLog('⏰ [AuctionDetailPopup] 기존 방식으로 카운트다운 시작');
      auctionCountdown();
    }
  }, [memoizedAuctionIndex, selectedLotData, timerControl, getTimerStatus, auctionCountdown, debugLog]);

  // 20초 카운트 취소
  const handleCountCancel = useCallback(() => {
    setShowCountModal(false);
  }, []);

  // 현장입찰 등록 모달 열기
  const handleBidderRegisterClick = useCallback(() => {
    setIsBidderEditMode(false);
    setBidderFormData({
      bidAmount: '',
      bidNumber: '',
      name: '',
      phone: '',
      email: '',
      memo: ''
    });
    setBidderFormErrors({});
    setShowBidderForm(true);
  }, []);

  // 입찰자 폼 닫기
  const handleBidderFormClose = useCallback(() => {
    setShowBidderForm(false);
  }, []);

  // 입찰자 폼 저장 (CMS 현장입찰) - 최적화된 버전
  const handleBidderFormSave = useCallback(() => {
    // 유효성 검사
    const errors: Partial<BidderFormData> = {};
    
    if (!isBidderEditMode) {
      // 등록 모드: 입찰가, 입찰번호 필수
      if (!bidderFormData.bidAmount.trim()) {
        errors.bidAmount = '입찰가를 입력해주세요.';
      }
      if (!bidderFormData.bidNumber.trim()) {
        errors.bidNumber = '입찰번호를 입력해주세요.';
      }
    } else {
      // 수정 모드: 입찰가, 입찰번호, 이름, 전화번호, 이메일 필수 (비고 제외)
      if (!bidderFormData.bidAmount.trim()) {
        errors.bidAmount = '입찰가를 입력해주세요.';
      }
      if (!bidderFormData.bidNumber.trim()) {
        errors.bidNumber = '입찰번호를 입력해주세요.';
      }
      if (!bidderFormData.name.trim()) {
        errors.name = '이름을 입력해주세요.';
      }
      if (!bidderFormData.phone.trim()) {
        errors.phone = '전화번호를 입력해주세요.';
      }
      if (!bidderFormData.email.trim()) {
        errors.email = '이메일을 입력해주세요.';
      }
    }

    if (Object.keys(errors).length > 0) {
      setBidderFormErrors(errors);
      return;
    }

    // CMS 현장입찰 실행
    if (!isBidderEditMode && memoizedAuctionIndex && selectedLotData?.index !== undefined) {
      const price = parseInt(bidderFormData.bidAmount.replace(/[^0-9]/g, ''));
      debugLog('🏢 [AuctionDetailPopup] CMS 현장입찰:', {
        auctionIndex: memoizedAuctionIndex,
        lotIndex: selectedLotData.index,
        price,
        code: bidderFormData.bidNumber
      });
      
      // 현장입찰 실행
      cmsPlaceBid(memoizedAuctionIndex, selectedLotData.index, price, bidderFormData.bidNumber);
      
      // 입찰 후 2초 뒤에 입찰 내역을 다시 조회하여 강제 새로고침
      setTimeout(() => {
        debugLog('🔄 [AuctionDetailPopup] 현장입찰 후 입찰내역 강제 새로고침:', {
          auctionIndex: memoizedAuctionIndex,
          lotIndex: selectedLotData.index
        });
        getBidHistory(memoizedAuctionIndex, selectedLotData.index);
      }, 2000);
    }

    setShowBidderForm(false);
  }, [isBidderEditMode, bidderFormData, memoizedAuctionIndex, selectedLotData, cmsPlaceBid, getBidHistory, debugLog]);

  // 입찰자 폼 입력 변경
  const handleBidderInputChange = useCallback((field: keyof BidderFormData, value: string) => {
    setBidderFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 에러 제거
    setBidderFormErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // 입찰내역 행 클릭 (수정 모드)
  const handleBidRowClick = useCallback((bidData: any) => {
    setIsBidderEditMode(true);
    // 입찰가에서 통화 기호와 콤마 제거하여 숫자만 추출
    const cleanBidAmount = bidData.bidAmountKrw.replace(/[₩,]/g, '');
    
    // 사전입찰자 코드 처리: F로 시작하는 경우 F와 앞의 0들을 제거하고 숫자만 추출
    let cleanSiteNumber = bidData.siteNumber;
    if (bidData.isPreBidder && cleanSiteNumber.startsWith('F')) {
      // F를 제거하고 숫자 부분 추출 (예: F0011 -> 11, F0005 -> 5)
      cleanSiteNumber = cleanSiteNumber.substring(1).replace(/^0+/, '') || '0';
    }
    
    setBidderFormData({
      bidAmount: cleanBidAmount, // 입찰가 (숫자만)
      bidNumber: cleanSiteNumber, // 입찰번호 (사전입찰자의 경우 F와 앞의 0 제거)
      name: bidData.bidder,
      phone: bidData.contact,
      email: bidData.email,
      memo: ''
    });
    setBidderFormErrors({});
    setShowBidderForm(true);
  }, []);

  // 입찰제한 토글 핸들러 (모달 열기)
  const handleBidRestrictionToggle = useCallback((key: string, bidderName: string, currentStatus: string, event: React.MouseEvent) => {
    event.stopPropagation(); // 행 클릭 이벤트 방지
    
    setPendingRestrictionChange({
      key,
      name: bidderName,
      currentStatus
    });
    setShowBidRestrictionModal(true);
  }, []);

  // 입찰제한/해제 확인 (사용자 차단 기능 사용) - 최적화된 버전
  const handleBidRestrictionConfirm = useCallback(() => {
    if (pendingRestrictionChange && memoizedAuctionIndex) {
      const { key, currentStatus } = pendingRestrictionChange;
      // 콜백으로 받은 현재 상태를 사용
      const newStatus = currentStatus === 'Y' ? 'N' : 'Y';
      
      // 입찰 내역에서 해당 사용자의 userIndex 찾기
      const targetBid = bidHistory.find(bid => bid._id === key);
      
      if (targetBid?.user?.index !== undefined) {
        const userIndex = targetBid.user.index;
        
        // 즉시 로컬 상태 업데이트 (Optimistic Update)
        if (newStatus === 'Y') {
          // 차단: bannedUsers에 추가
          setBannedUsers(prev => {
            if (!prev.includes(userIndex)) {
              debugLog('🚫 [AuctionDetailPopup] 로컬 상태 즉시 업데이트 - 차단:', userIndex);
              return [...prev, userIndex];
            }
            return prev;
          });
          // 대기 중인 액션 추가
          setPendingBanActions(prev => ({ ...prev, [userIndex]: 'ban' }));
        } else {
          // 차단 해제: bannedUsers에서 제거
          setBannedUsers(prev => {
            const filtered = prev.filter(idx => idx !== userIndex);
            debugLog('✅ [AuctionDetailPopup] 로컬 상태 즉시 업데이트 - 차단 해제:', userIndex);
            return filtered;
          });
          // 대기 중인 액션 추가
          setPendingBanActions(prev => ({ ...prev, [userIndex]: 'unban' }));
        }
        
        // 서버에 요청 전송
        if (newStatus === 'Y') {
          debugLog('🚫 [AuctionDetailPopup] 사용자 차단:', {
            auctionIndex: memoizedAuctionIndex,
            userIndex: targetBid.user.index
          });
          banUser(memoizedAuctionIndex, targetBid.user.index);
        } else {
          // 사용자 차단 해제
          debugLog('✅ [AuctionDetailPopup] 사용자 차단 해제:', {
            auctionIndex: memoizedAuctionIndex,
            userIndex: targetBid.user.index
          });
          unbanUser(memoizedAuctionIndex, targetBid.user.index);
        }
      } else {
        debugLog('⚠️ [AuctionDetailPopup] 사전입찰자는 차단할 수 없습니다:', {
          bidId: key,
          hasUser: !!targetBid?.user
        });
        toast.warning('사전입찰자는 차단할 수 없습니다.');
      }
    }
    
    setShowBidRestrictionModal(false);
    setPendingRestrictionChange(null);
  }, [pendingRestrictionChange, memoizedAuctionIndex, bidHistory, banUser, unbanUser, debugLog]);

  // 입찰제한/해제 취소
  const handleBidRestrictionCancel = useCallback(() => {
    setShowBidRestrictionModal(false);
    setPendingRestrictionChange(null);
  }, []);

  // 엑셀 다운로드 핸들러
  const handleExcelDownload = useCallback(() => {
    if (!currentLot || !currentLot.bidHistory || currentLot.bidHistory.length === 0) {
      alert('다운로드할 입찰 내역이 없습니다.');
      return;
    }

    try {
      debugLog('📊 [AuctionDetailPopup] 엑셀 다운로드 시작:', {
        lotId: currentLot.lotId,
        bidHistoryCount: currentLot.bidHistory.length
      });

      // 엑셀 데이터 준비
      const excelData = currentLot.bidHistory.map((bid) => ({
        'No': bid.no,
        '입찰일시': bid.bidTime,
        '입찰자': bid.bidder,
        '현장번호': bid.siteNumber || '-',
        '국가': bid.country,
        '입찰금액(KRW)': bid.bidAmountKrw.replace(/[₩,]/g, ''),
        '입찰금액(USD)': bid.bidAmountUsd.replace(/[$,]/g, ''),
        '연락처': bid.contact || '-',
        '이메일': bid.email || '-',
        '입찰제한': (bidRestrictions[bid.key] || 'N') === 'Y' ? '제한' : '',
        '입찰자유형': bid.isPreBidder ? '사전입찰자' : '실시간입찰자'
      }));

      // 워크북 생성
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // 컬럼 너비 설정
      const columnWidths = [
        { wch: 5 },   // No
        { wch: 20 },  // 입찰일시
        { wch: 15 },  // 입찰자
        { wch: 12 },  // 현장번호
        { wch: 10 },  // 국가
        { wch: 18 },  // 입찰금액(KRW)
        { wch: 18 },  // 입찰금액(USD)
        { wch: 15 },  // 연락처
        { wch: 25 },  // 이메일
        { wch: 10 },  // 입찰제한
        { wch: 15 }   // 입찰자유형
      ];
      worksheet['!cols'] = columnWidths;

      // 워크시트를 워크북에 추가
      XLSX.utils.book_append_sheet(workbook, worksheet, '입찰내역');

      // 파일명 생성 (현재 날짜와 LOT ID 포함)
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
      const fileName = `입찰내역_${currentLot.lotId}_${dateStr}_${timeStr}.xlsx`;

      // 엑셀 파일 다운로드
      XLSX.writeFile(workbook, fileName);

      debugLog('✅ [AuctionDetailPopup] 엑셀 다운로드 완료:', {
        fileName,
        recordCount: excelData.length
      });

    } catch (error) {
      devError('엑셀 다운로드 중 오류:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  }, [currentLot, bidRestrictions, debugLog]);

  // 경매시작 핸들러 (개발모드에서만 사용)
  const handleStartBid = useCallback(() => {
    if (!memoizedAuctionIndex || !selectedLotData?.lotNo) {
      debugLog('❌ [AuctionDetailPopup] 경매시작 실패 - 필수 정보 없음:', {
        auctionIndex: memoizedAuctionIndex,
        lotNo: selectedLotData?.lotNo
      });
      alert('경매를 시작할 수 없습니다. 경매 인덱스나 작품 번호가 없습니다.');
      return;
    }

    debugLog('🏁 [AuctionDetailPopup] 경매시작 호출:', {
      auctionIndex: memoizedAuctionIndex,
      lotNo: selectedLotData.lotNo
    });
    
    startBid(memoizedAuctionIndex, selectedLotData.lotNo);
  }, [memoizedAuctionIndex, selectedLotData, startBid, debugLog]);

  // 수동 재시도 함수
  const handleRetryRequest = useCallback(() => {
    debugLog('🔄 [AuctionDetailPopup] 수동 재시도 요청');
    hasManuallyRequestedAuctionInfo.current = false;
    lastManuallyRequestedAuctionIndex.current = null;
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
    requestAuctionData();
  }, [debugLog, requestAuctionData]);

  const renderContent = () => {
    // 소켓 연결 상태 확인
    if (!isConnected) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>소켓 연결 중...</p>
        </div>
      );
    }

    // 데이터 로딩 중
    if (!socketAuctionData || auctionLots.length === 0) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>경매 데이터를 불러오는 중...</p>
          <button 
            onClick={handleRetryRequest}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
        </div>
      );
    }

    // 현재 LOT이 없는 경우
    if (!currentLot) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>선택된 작품이 없습니다.</p>
        </div>
      );
    }

    return (
      <PopupContainer>
        {/* 왼쪽 영역 - 경매 Lots */}
        <AuctionLotsList
          lots={auctionLots}
          selectedLotId={selectedLotId}
          auctionStartAt={socketAuctionData?.auctionStartAt} // 경매 시작 시간 전달
          auctionEndAt={socketAuctionData?.auctionEndAt} // 경매 종료 시간 전달
          currentLot={socketCurrentLot} // 소켓에서 받은 현재 진행 중인 lot 전달
          isAuctionEnded={isAuctionEnded} // 소켓에서 받은 경매 종료 상태 전달
          onLotSelect={handleLotSelect}
          onAuctionEnded={handleAuctionEnded} // 경매 종료 시 콜백 전달
        />

        {/* 오른쪽 영역 - 선택된 LOT 상세 정보 */}
        <RightSection>
          {/* 현재 로트 정보 */}
          <CurrentLotInfo 
            currentLot={currentLot} 
            auctionEndAt={socketAuctionData?.auctionEndAt}
          />

          {/* 입찰내역 탭바 및 테이블 */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
            {/* 탭바 */}
            <div style={{ 
              display: 'flex', 
              borderBottom: '1px solid #ddd',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => handleTabChange('live')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'transparent',
                  color: activeTab === 'live' ? '#333' : '#999',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'live' ? '2px solid #007bff' : '2px solid transparent',
                  fontSize: '14px',
                  fontWeight: activeTab === 'live' ? 'bold' : 'normal'
                }}
              >
                라이브입찰
              </button>
              <button
                onClick={() => handleTabChange('pre')}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  background: 'transparent',
                  color: activeTab === 'pre' ? '#333' : '#999',
                  cursor: 'pointer',
                  borderBottom: activeTab === 'pre' ? '2px solid #007bff' : '2px solid transparent',
                  fontSize: '14px',
                  fontWeight: activeTab === 'pre' ? 'bold' : 'normal'
                }}
              >
                사전입찰
              </button>
            </div>

            {/* 입찰내역 테이블 */}
            {(() => {
              // BidHistoryTable 렌더링 직전 디버깅 로그
              if (isDebugMode) {
                debugLog('🔍 [AuctionDetailPopup] BidHistoryTable 렌더링 정보:', {
                  selectedLotId,
                  selectedLotDataIndex: selectedLotData?.index,
                  selectedLotDataType: typeof selectedLotData?.index,
                  preBidsLength: socketAuctionData?.preBids?.length || 0,
                  preBids: socketAuctionData?.preBids,
                  exchangeRate: selectedLotData?.exchangeRate,
                  activeTab,
                  liveBidHistoryLength: currentLot.bidHistory.length,
                  preBidHistoryLength: preBidHistory.length
                });
              }
              return null;
            })()}
            
            {activeTab === 'live' ? (
              <BidHistoryTable
                bidHistory={currentLot.bidHistory}
                bidRestrictions={bidRestrictions}
                bannedUsers={bannedUsers} // 차단된 사용자 목록 전달
                lotStatus={currentLot.status} // 현재 로트 상태 전달
                auctionStartAt={socketAuctionData?.auctionStartAt} // 경매 시작 시간 전달
                auctionIndex={memoizedAuctionIndex} // ban-user 소켓 이벤트를 위한 auctionIndex 전달
                preBids={socketAuctionData?.preBids} // 사전입찰 정보 전달
                currentLotIndex={selectedLotData?.index} // 실제 lot index 전달
                exchangeRate={selectedLotData?.exchangeRate} // 환율 정보 전달
                onExcelDownload={handleExcelDownload}
                onBidderRegister={handleBidderRegisterClick}
                onCountStart={handleCountClick}
                // onEndAuction={handleEndAuctionClick} // 소켓 카운트다운으로 자동 처리되므로 제거
                onBidRowClick={handleBidRowClick}
                onBidRestrictionToggle={handleBidRestrictionToggle}
                onBanUser={banUser} // ban-user 소켓 이벤트 핸들러 전달
                onUnbanUser={unbanUser} // unban-user 소켓 이벤트 핸들러 전달
                onStartBid={isDebugMode ? handleStartBid : undefined} // 개발모드에서만 전달
              />
            ) : (
              <BidHistoryTable
                bidHistory={preBidHistory}
                bidRestrictions={{}} // 사전입찰은 입찰제한 없음
                bannedUsers={[]} // 사전입찰은 차단된 사용자 없음
                lotStatus={currentLot.status}
                auctionStartAt={socketAuctionData?.auctionStartAt}
                auctionIndex={memoizedAuctionIndex}
                preBids={[]} // 사전입찰 모드에서는 빈 배열
                currentLotIndex={0}
                exchangeRate={selectedLotData?.exchangeRate || 1200}
                onExcelDownload={handleExcelDownload}
                onBidderRegister={() => {}} // 빈 함수
                onCountStart={() => {}} // 빈 함수  
                onBidRowClick={() => {}} // 빈 함수
                onBidRestrictionToggle={() => {}} // 빈 함수
                // 사전입찰용 옵션들
                isPreBidMode={true}
                hideSiteNumber={true}
                hideBidRestriction={true}
                hideActionButtons={true}
              />
            )}
          </div>
        </RightSection>
      </PopupContainer>
    );
  };

  return (
    <CmsPopup
      title="실시간 입찰 내역"
      isOpen={isOpen}
      onClose={onClose}
      width={"1400px"}
      showRequiredMark={false}
      leftButtons={[]}
      rightButtons={[
        {
          label: '닫기',
          onClick: onClose,
          type: CmsPopupBtnType.SECONDARY,
        },
      ]}
    >
      {renderContent()}
      
      {/* 경매 종료 확인 모달 */}
      {/* <Modal
        isOpen={showEndAuctionModal}
        onClose={handleEndAuctionCancel}
        header="경매를 종료하시겠습니까?"
        body={`종료 시, 현재 최고가 입찰자에게 낙찰됩니다.`}
        bgColor="#ffffff"
        zIndex={10002}
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleEndAuctionCancel}
            >
              취소
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleEndAuctionConfirm}
            >
              경매 종료
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      /> */}

      {/* 20초 카운트 확인 모달 */}
      <Modal
        isOpen={showCountModal}
        onClose={handleCountCancel}
        header="카운트를 시작하시겠습니까?"
        body={`실시간으로 참가자들에게\n남은 시간이 안내됩니다.`}
        bgColor="#ffffff"
        zIndex={10002}
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleCountCancel}
            >
              취소
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleCountConfirm}
            >
              시작
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      />

      {/* 입찰제한 확인 모달 */}
      <Modal
        isOpen={showBidRestrictionModal}
        onClose={handleBidRestrictionCancel}
        header={pendingRestrictionChange?.currentStatus === 'Y' ? '입찰 제한 해제 확인' : `${pendingRestrictionChange?.name}님을 입찰 제한 하시겠습니까?`}
        body={
          pendingRestrictionChange?.currentStatus === 'Y' 
            ? `${pendingRestrictionChange.name}님의 입찰 제한을\n해제하시겠습니까?`
            : `입찰 제한 시 해당 사용자는\n이번 경매 입찰에 참여할 수 없습니다.`
        }
        bgColor="#ffffff"
        zIndex={10002}
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleBidRestrictionCancel}
            >
              취소
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleBidRestrictionConfirm}
            >
              {pendingRestrictionChange?.currentStatus === 'Y' ? '제한해제' : '제한'}
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      />

      {/* 현장 입찰자 등록/수정 팝업 */}
      <BidderFormPopup
        isOpen={showBidderForm}
        isEditMode={isBidderEditMode}
        formData={bidderFormData}
        formErrors={bidderFormErrors}
        onClose={handleBidderFormClose}
        onSave={handleBidderFormSave}
        onInputChange={handleBidderInputChange}
      />
    </CmsPopup>
  );
};

export default AuctionDetailPopup;
